import React from "react";
import styled from "styled-components";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalFormGrid,
    StandardModalFormGroup,
    StandardModalLabel
} from "src/components";
import { FeedAudienceSelect } from "src/components/NoticeboardFeedUi";
import { portalColors } from "src/components/BusinessPortalComponents";
import { CHECK_IN_CATEGORY_LIST } from "../checkIn";
import SearchIcon from "src/img/search-icon.svg";

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
    value: String(index + 1),
    label: new Date(2026, index, 1).toLocaleString("en", {
        month: "long"
    })
}));

export const EMPTY_NOTICE_FILTERS = {
    search: "",
    checkInCategories: [],
    myPostsOnly: false,
    month: "",
    year: ""
};

export const getActiveNoticeFilterCount = (filters, canUseCheckedIn = true) => (
    (filters.search ? 1 : 0) +
    (canUseCheckedIn && filters.checkInCategories.length ? 1 : 0) +
    (filters.myPostsOnly ? 1 : 0) +
    (filters.month ? 1 : 0) +
    (filters.year ? 1 : 0)
);

const SearchField = styled.div`
    position: relative;
    width: 100%;
`;

const SearchInput = styled.input`
    width: 100%;
    height: 46px;
    min-width: 0;
    box-sizing: border-box;
    padding: 0 3rem 0 1rem;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    outline: none;

    &::placeholder {
        color: ${portalColors.textSoft};
        font-weight: 500;
    }

    &:hover {
        border-color: ${portalColors.textSoft};
    }

    &:focus,
    &:focus-visible {
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
    }
`;

const SearchIconWrapper = styled.span`
    position: absolute;
    top: 50%;
    right: 1rem;
    width: 1.1rem;
    height: 1.1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transform: translateY(-50%);
    pointer-events: none;

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const FilterCategoryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;

    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
`;

const FilterCategoryButton = styled.button`
    width: 100%;
    min-height: 3rem;
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.7rem 0.85rem;
    border: 1px solid ${p => p.$active ? p.$color : portalColors.borderStrong};
    border-radius: 8px;
    background: ${p => p.$active ? p.$softColor : portalColors.surface};
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25;
    text-align: left;
    cursor: pointer;
    transition:
        background 0.16s ease,
        border-color 0.16s ease,
        box-shadow 0.16s ease,
        transform 0.16s ease;

    &:hover {
        border-color: ${p => p.$color};
        background: ${p => p.$active ? p.$softColor : portalColors.surfaceSoft};
        transform: translateY(-1px);
    }

    &:focus-visible {
        outline: none;
        border-color: ${p => p.$color};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
    }
`;

const FilterCategoryCheckbox = styled.span`
    width: 1.15rem;
    height: 1.15rem;
    flex: 0 0 1.15rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid ${p => p.$active ? p.$color : portalColors.borderStrong};
    border-radius: 4px;
    background: ${p => p.$active ? p.$color : portalColors.surface};
    color: #fff;
    font-size: 0.75rem;
    font-weight: 800;
    line-height: 1;
`;

const CategoryIcon = styled.span`
    width: 1.7rem;
    height: 1.7rem;
    flex: 0 0 1.7rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: ${p => p.$color};
    overflow: hidden;

    img {
        display: block;
        width: 1rem;
        height: 1rem;
        object-fit: contain;
    }
`;

const CategoryText = styled.span`
    min-width: 0;
    overflow-wrap: anywhere;
`;

const NoticeboardFiltersModal = ({
    open,
    filters,
    canUseCheckedIn,
    yearOptions,
    onClose,
    onChange,
    onToggleCategory,
    onApply,
    onClear
}) => (
    <StandardModal
        open={open}
        onClose={onClose}
        title="Noticeboard Filters"
        widthMode="medium"
        footer={(
            <StandardModalActionGroup $pushRight>
                <StandardModalActionButton type="button" onClick={onClear}>
                    Clear Filters
                </StandardModalActionButton>
                <StandardModalActionButton type="button" buttonType="primary" onClick={onApply}>
                    Apply Filters
                </StandardModalActionButton>
            </StandardModalActionGroup>
        )}
    >
        <StandardModalFormGroup>
            <StandardModalLabel>Search</StandardModalLabel>
            <SearchField>
                <SearchInput
                    type="search"
                    value={filters.search}
                    placeholder={canUseCheckedIn ? "Search posts or places" : "Search posts"}
                    aria-label={canUseCheckedIn ? "Search posts or places" : "Search posts"}
                    onChange={event => onChange(current => ({ ...current, search: event.target.value }))}
                />
                <SearchIconWrapper aria-hidden="true">
                    <img src={SearchIcon} alt="" />
                </SearchIconWrapper>
            </SearchField>
        </StandardModalFormGroup>

        {canUseCheckedIn && (
            <StandardModalFormGroup>
                <StandardModalLabel>Check In Categories</StandardModalLabel>
                <FilterCategoryGrid>
                    {CHECK_IN_CATEGORY_LIST.map(category => {
                        const active = filters.checkInCategories.includes(category.id);

                        return (
                            <FilterCategoryButton
                                key={category.id}
                                type="button"
                                $active={active}
                                $color={category.color}
                                $softColor={category.softColor}
                                aria-pressed={active}
                                onClick={() => onToggleCategory(category.id)}
                            >
                                <FilterCategoryCheckbox $active={active} $color={category.color}>
                                    {active ? "✓" : ""}
                                </FilterCategoryCheckbox>
                                <CategoryIcon $color={category.color}>
                                    <img src={category.icon} alt="" />
                                </CategoryIcon>
                                <CategoryText>{category.label}</CategoryText>
                            </FilterCategoryButton>
                        );
                    })}
                </FilterCategoryGrid>
            </StandardModalFormGroup>
        )}

        <StandardModalFormGroup>
            <StandardModalLabel>Post Ownership</StandardModalLabel>
            <FilterCategoryGrid>
                <FilterCategoryButton
                    type="button"
                    $active={filters.myPostsOnly}
                    $color={portalColors.blue}
                    $softColor={portalColors.blueSoft}
                    aria-pressed={filters.myPostsOnly}
                    onClick={() => onChange(current => ({
                        ...current,
                        myPostsOnly: !current.myPostsOnly
                    }))}
                >
                    <FilterCategoryCheckbox
                        $active={filters.myPostsOnly}
                        $color={portalColors.blue}
                    >
                        {filters.myPostsOnly ? "✓" : ""}
                    </FilterCategoryCheckbox>
                    <CategoryText>User Posts Only</CategoryText>
                </FilterCategoryButton>
            </FilterCategoryGrid>
        </StandardModalFormGroup>

        <StandardModalFormGroup>
            <StandardModalLabel>Date</StandardModalLabel>
            <StandardModalFormGrid columns={2}>
                <FeedAudienceSelect
                    value={filters.month}
                    onChange={event => onChange(current => ({ ...current, month: event.target.value }))}
                >
                    <option value="">Any month</option>
                    {MONTH_OPTIONS.map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                </FeedAudienceSelect>

                <FeedAudienceSelect
                    value={filters.year}
                    onChange={event => onChange(current => ({ ...current, year: event.target.value }))}
                >
                    <option value="">Any year</option>
                    {yearOptions.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </FeedAudienceSelect>
            </StandardModalFormGrid>
        </StandardModalFormGroup>
    </StandardModal>
);

export default NoticeboardFiltersModal;
